import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 6;

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | success | error | timeout
  const bookingIdRef = useRef(null);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        bookingIdRef.current = res.data.booking_id;
        if (res.data.payment_status === "paid") { setStatus("success"); return; }
        if (res.data.status === "expired") { setStatus("error"); return; }
        if (attempts >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <div className="bg-white border border-black/10 rounded-2xl p-10 text-center max-w-sm w-full">
        {status === "checking" && (<>
          <Loader2 className="w-12 h-12 text-[#FF5A00] animate-spin mx-auto" />
          <h1 className="font-display text-2xl font-bold mt-4">Confirming payment…</h1>
          <p className="text-muted-foreground text-sm mt-2">Hang tight, this only takes a moment.</p>
        </>)}
        {status === "success" && (<>
          <CheckCircle2 className="w-14 h-14 text-[#2E7D32] mx-auto" />
          <h1 data-testid="payment-success" className="font-display text-2xl font-bold mt-4">Payment successful!</h1>
          <p className="text-muted-foreground text-sm mt-2">Your rental is confirmed.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-6 w-full bg-[#0A0A0A] text-white rounded-full py-3 font-medium hover:bg-[#FF5A00] transition-colors">Go to dashboard</button>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="w-14 h-14 text-destructive mx-auto" />
          <h1 data-testid="payment-error" className="font-display text-2xl font-bold mt-4">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
          <p className="text-muted-foreground text-sm mt-2">{status === "timeout" ? "Check your dashboard shortly for confirmation." : "We couldn't confirm your payment."}</p>
          <button onClick={() => navigate("/dashboard")} className="mt-6 w-full bg-[#0A0A0A] text-white rounded-full py-3 font-medium hover:bg-[#FF5A00] transition-colors">Back to dashboard</button>
        </>)}
      </div>
    </div>
  );
}
